import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { isEqual } from 'lodash/fp';
import { useUpdateBuyOrder } from 'services/buy-process';
import { RootState } from '../../../../global';
import { FiscalYear } from '../../../../global/entities/BuyOrder';
import { useBuyProcessForm } from '../../buy-process-form-utils';
import { saveEditFiscalYear } from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useFetchClosingDateOptions,
} from '../../use-buy-process-form';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import {
  fiscalYearValidationSchema,
  formatFirstClosingDate,
} from './fiscal-year-utils';

export const useFiscalYear = () => {
  const dispatch = useDispatch();
  const { setRequestedStep, id, goToRequestedStep } = useBuyProcessForm();
  const initialState = useSelector(
    (state: RootState) => state.buyOrderEdit[id].fiscalYear,
  );

  const [fiscalYear, setFiscalYear] = useState(
    initialState.fiscalYear || FiscalYear.JanuaryStart,
  );

  const { control, handleSubmit, errors, reset, trigger, watch, setValue } = useForm<{
      firstClosingDate: string;
    }>({
      defaultValues: { ...initialState },
      resolver: yupResolver(fiscalYearValidationSchema),
    });

  const { closingDateOptions } = useFetchClosingDateOptions();
  useEffect(() => {
    setValue(
      'firstClosingDate',
      initialState.firstClosingDate
        ? formatFirstClosingDate(initialState.firstClosingDate)
        : initialState.extendedFiscalYear
          ? formatFirstClosingDate(closingDateOptions[fiscalYear].regularDate)
          : formatFirstClosingDate(
            closingDateOptions[fiscalYear].extendedFiscalYearDate,
          ),
    );
    // eslint-disable-next-line
  }, []);

  const firstClosingDate = watch('firstClosingDate');
  const { extendedFiscalYearDate } = closingDateOptions[fiscalYear];
  const handleFiscalYearSubmit = useCallback(() => {
    const isExtendedFiscalYearSelected = !!extendedFiscalYearDate
      && isEqual(
        formatFirstClosingDate(dayjs(firstClosingDate).toISOString()),
        formatFirstClosingDate(extendedFiscalYearDate),
      );
    dispatch(
      saveEditFiscalYear({
        id,
        fiscalYear,
        firstClosingDate,
        extendedFiscalYear: isExtendedFiscalYearSelected,
      }),
    );
  }, [extendedFiscalYearDate, dispatch, firstClosingDate, fiscalYear, id]);

  useEffect(() => {
    goToRequestedStep(trigger, handleFiscalYearSubmit);
  }, [goToRequestedStep, handleFiscalYearSubmit, trigger]);

  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    control,
    errors,
    reset,
    closingDateOptions: closingDateOptions[fiscalYear],
    fiscalYear,
    setFiscalYear,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: handleSubmit(() => {
      handleFiscalYearSubmit();
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, trigger, e),
  };
};
